import { storyblokEditable } from '@storyblok/react';
import GoogleMapReact from 'google-map-react';
import { useEffect, useRef, useState } from 'react';

import ButtonArrow from '@/components/elements/ButtonArrow';
import Container from '@/components/elements/Container';
import HeadingLayout from '@/components/elements/Heading';
import Heading from '@/components/elements/Heading';
import Prose from '@/components/elements/Prose';
import Section from '@/components/elements/Section';
import StandardText, {
  StandardTextSize,
} from '@/components/elements/StandardText';

function LocationPin({ lat, lng }) {
  return (
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 108.07 128.79"
        className="-translate-x-[50%] -translate-y-[100%] w-[24px]"
      >
        <path
          className="cls-1"
          fill="#c10007"
          fillRule="evenodd"
          d="M15.83,15.83a54,54,0,0,0,0,76.42L51.18,127.6a4,4,0,0,0,5.72,0L92.25,92.25A54,54,0,0,0,15.83,15.83ZM30.4,52.63A24.25,24.25,0,1,1,54.65,76.87,24.25,24.25,0,0,1,30.4,52.63Z"
        />
      </svg>
      <span className="hidden">
        {lat}, {lng}
      </span>
    </div>
  );
}

function GoogleMap({ map_key, locations, center, zoom }) {
  return (
    <GoogleMapReact
      bootstrapURLKeys={{ key: map_key }}
      defaultCenter={center}
      center={center}
      defaultZoom={zoom}
      zoom={zoom}
    >
      {locations.map((l) => (
        <LocationPin
          key={`marker-${l.uid}`}
          lat={parseFloat(l.latitude)}
          lng={parseFloat(l.longitude)}
        />
      ))}
    </GoogleMapReact>
  );
}

function ContactForm({ blok }) {
  const [submitted, setSubmitted] = useState(false);
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  const nameRef = useRef<HTMLInputElement>();
  const emailRef = useRef<HTMLInputElement>();
  const reasonRef = useRef<HTMLSelectElement>();
  const messageRef = useRef<HTMLTextAreaElement>();

  useEffect(() => {
    async function submit() {
      setLoading(true);
      const response = await fetch(`/api/contact`, {
        method: 'POST',
        body: JSON.stringify({
          name: nameRef.current.value,
          email: emailRef.current.value,
          reason: reasonRef.current.value,
          message: messageRef.current.value,
        }),
      }).then((res) => res.json());

      setLoading(false);
      setComplete(response.submission);
    }

    if (submitted && !loading) {
      submit();
    }
  }, [submitted]);

  return (
    <>
      {loading && <HeadingLayout>Sending...</HeadingLayout>}
      {complete && (
        <div className="w-1/2">
          <StandardText
            blok={{
              headline: blok.completion_headline,
              content: blok.completion_message,
              actions: blok.actions,
            }}
            size={StandardTextSize.MD}
          />
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(() => true);
        }}
        className={`mt-4 ${loading || complete ? 'hidden' : ''}`}
      >
        <label
          className="block mt-1 font-semibold text-theme-gray-medium"
          htmlFor="name"
        >
          Name
        </label>
        <input
          className="block"
          ref={nameRef}
          id="name"
          name="name"
          type="text"
          required={true}
          placeholder="Enter Name"
        />
        <label
          className="block font-semibold text-theme-gray-medium"
          htmlFor="email"
        >
          Email
        </label>
        <input
          className="block"
          ref={emailRef}
          name="email"
          type="email"
          required={true}
          placeholder="Enter Email"
        />
        <label
          className="block font-semibold text-theme-gray-medium"
          htmlFor="reason"
        >
          Reason for Inquiry
        </label>
        <select className="block" ref={reasonRef} name="reason" required={true}>
          <option selected>General Inquiry</option>
          {blok.reasons.map((b) => {
            return (
              <option key={b} value={b}>
                {b}
              </option>
            );
          })}
        </select>
        <label
          className="block font-semibold text-theme-gray-medium"
          htmlFor="message"
        >
          How can we help?
        </label>
        <textarea
          className="block"
          ref={messageRef}
          name="message"
          placeholder="Your message"
          required={true}
          minLength={10}
          maxLength={2000}
        ></textarea>
        <button className="btn btn-primary">
          <ButtonArrow className={`mr-1 -ml-1 h-5 w-6`} />
          <span className="-mb-0.5">Submit</span>
        </button>
      </form>
    </>
  );
}

export default function Contact({ blok }) {
  return (
    <Section {...storyblokEditable(blok)} gapTop="none" className="space-y-24">
      <Container
        size={'widest'}
        className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-16 lg:grid-cols-6 lg:gap-24"
      >
        <div className="col-span-1 lg:col-span-2">
          <StandardText
            blok={{
              heading: blok.headline,
              content: blok.content,
            }}
          />
        </div>
        <div className="col-span-1 grid md:col-span-2 lg:col-span-3">
          <ContactForm blok={blok} />
        </div>
      </Container>
      <Container size={'base'} className="space-y-8">
        <Heading size="sm">Locations</Heading>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-16 lg:gap-24">
          {blok.locations?.map((p) => (
            <Prose key={p.uid} size="md" content={p.content} />
          ))}
        </div>
      </Container>
      <Container size={'base'}>
        <div className="h-[320px] w-full md:h-[640px]">
          <GoogleMap
            map_key={blok.google_maps_key}
            center={[
              parseFloat(blok.center_latitude),
              parseFloat(blok.center_longitude),
            ]}
            zoom={parseFloat(blok.zoom)}
            locations={blok.locations}
          />
        </div>
      </Container>
    </Section>
  );
}
